/**
 * Today's challenge card (specs/home-page Story 2): the day's prepared slate, a countdown to the next
 * one, "Send to a Friend" and "Beat the Monkey" (AC-10b). Kicked-off matches are hidden (AC-11); with
 * none left the card shows the empty copy and no buttons.
 */

import React, { useMemo } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Zap, UserPlus, ChevronRight } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { HOME_COPY, HOME_TEST_IDS } from '../../../src/home/constants/home.constants';
import { formatNewIn } from '../../../src/home/helpers/homeCountdown.helper';
import { visibleSlateMatches } from '../../../src/home/helpers/slateToPickSlips.helper';
import type { TodaySlateView } from '../../../src/home/types/home.types';
import { HomeRegionError } from '../HomeRegionState/HomeRegionError';
import { HomeRegionSkeleton } from '../HomeRegionState/HomeRegionSkeleton';
import { SlateMatchRow } from './SlateMatchRow';
import { BeatTheMonkeyButton } from './BeatTheMonkeyButton';
import { SLATE_GRADIENT_END, getTodayChallengeCardStyles } from './TodayChallengeCard.styles';

const LABEL_ICON_SIZE = 12;
const BUTTON_ICON_SIZE = 15;
const SKELETON_ROWS = 2;
const SEE_ALL_ICON_SIZE = 13;
const SEE_ALL_HIT_SLOP = 8;

export interface TodayChallengeCardProps {
  slate: TodaySlateView | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** "Send to a Friend": the container stashes the visible slate and opens Create at the rival step. */
  onSend: () => void;
  /** "Beat the Monkey": the container stashes the slate with the Monkey as rival and opens Create at review. */
  onBeatMonkey: () => void;
  /** "See all matches": the container opens the Fixtures screen (AC-11b). */
  onSeeAll: () => void;
  /** Frozen clock for tests / Storybook; defaults to now. */
  nowMs?: number;
}

export const TodayChallengeCard: React.FC<TodayChallengeCardProps> = ({
  slate,
  isLoading,
  isError,
  onRetry,
  onSend,
  onBeatMonkey,
  onSeeAll,
  nowMs,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getTodayChallengeCardStyles(theme), [theme]);
  // Read per render: the card re-renders on every refetch, which keeps the countdown and the
  // kicked-off filter honest.
  const now = nowMs ?? Date.now();
  const visible = visibleSlateMatches(slate, now);

  const renderBody = () => {
    if (isLoading) return <HomeRegionSkeleton rows={SKELETON_ROWS} testID={HOME_TEST_IDS.slateSkeleton} />;
    if (isError) return <HomeRegionError onRetry={onRetry} testID={`${HOME_TEST_IDS.regionError}-slate`} />;
    if (visible.length === 0) {
      return (
        <Text style={styles.emptyText} testID={HOME_TEST_IDS.slateEmpty}>
          {HOME_COPY.SLATE_EMPTY}
        </Text>
      );
    }
    return (
      <>
        {visible.map((match, index) => (
          <SlateMatchRow key={match.matchId} match={match} isFirst={index === 0} styles={styles} />
        ))}
        <View style={styles.buttonsRow}>
          <Pressable
            style={({ pressed, hovered }) => [
              styles.sendButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.sendButtonPressed,
              hovered && Platform.OS === 'web' && styles.sendButtonHover,
            ]}
            onPress={onSend}
            accessibilityRole="button"
            accessibilityLabel={HOME_COPY.SLATE_SEND_A11Y}
            testID={HOME_TEST_IDS.slateSend}
          >
            <UserPlus size={BUTTON_ICON_SIZE} color={theme.onPrimary} strokeWidth={2.4} />
            <Text style={styles.sendText}>{HOME_COPY.SLATE_SEND}</Text>
            <ChevronRight size={BUTTON_ICON_SIZE} color={theme.onPrimary} strokeWidth={2.4} />
          </Pressable>
          <BeatTheMonkeyButton onPress={onBeatMonkey} styles={styles} theme={theme} />
        </View>
      </>
    );
  };

  return (
    <LinearGradient colors={[theme.background.card, SLATE_GRADIENT_END]} style={styles.card} testID={HOME_TEST_IDS.slateCard}>
      <View style={styles.headerRow}>
        <View style={styles.headerText}>
          <View style={styles.labelRow}>
            <Zap size={LABEL_ICON_SIZE} color={theme.accent} fill={theme.accent} />
            <Text style={styles.label}>{HOME_COPY.SLATE_LABEL}</Text>
          </View>
          <Text style={styles.subtitle} numberOfLines={1}>{HOME_COPY.SLATE_SUBTITLE}</Text>
        </View>
        {slate && (
          <Text style={styles.newIn} testID={HOME_TEST_IDS.slateCountdown}>
            {HOME_COPY.SLATE_NEW_IN} {formatNewIn(slate.nextSlateAt, now)}
          </Text>
        )}
      </View>
      {renderBody()}
      {/* Home's only way into Fixtures (AC-11b), so it shows in every state of the card. */}
      <Pressable
        style={({ pressed, hovered }) => [
          styles.seeAllButton,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.seeAllPressed,
          hovered && Platform.OS === 'web' && styles.seeAllHover,
        ]}
        onPress={onSeeAll}
        hitSlop={SEE_ALL_HIT_SLOP}
        accessibilityRole="link"
        accessibilityLabel={HOME_COPY.SLATE_SEE_ALL_A11Y}
        testID={HOME_TEST_IDS.slateSeeAll}
      >
        <Text style={styles.seeAllText}>{HOME_COPY.SLATE_SEE_ALL}</Text>
        <ChevronRight size={SEE_ALL_ICON_SIZE} color={theme.gray[500]} strokeWidth={2.4} />
      </Pressable>
    </LinearGradient>
  );
};
